import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  getProjects,
  getProject,
  getCategories,
  getServices,
  getBlogPosts,
  getBlogPost,
  getBlogCategories,
  getPortfolioItems,
  getPortfolioItem,
  getPortfolioCategories,
  getCompanyInfo
} from '../api/api';

const DataContext = createContext();

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  const [data, setData] = useState({
    projects: [],
    categories: [],
    services: [],
    blogPosts: [],
    blogCategories: [],
    portfolioItems: [],
    portfolioCategories: [],
    companyInfo: null,
    loading: true,
    error: null
  });

  useEffect(() => {
    // Load all data once when app starts
    const loadAllData = async () => {
      try {
        console.log('Loading all data...');
        
        // First, load list data
        const [
          projectsListRes,
          categoriesRes,
          servicesRes,
          blogPostsListRes,
          blogCategoriesRes,
          portfolioItemsListRes,
          portfolioCategoriesRes,
          companyInfoRes
        ] = await Promise.all([
          getProjects({ page_size: 1000 }), // Get all projects
          getCategories(),
          getServices(),
          getBlogPosts({ page_size: 1000 }), // Get all blog posts
          getBlogCategories(),
          getPortfolioItems({ page_size: 1000 }), // Get all portfolio items
          getPortfolioCategories(),
          getCompanyInfo()
        ]);

        // Extract data from responses (handle both array and paginated responses)
        const extractData = (response) => {
          const data = response.data;
          return Array.isArray(data) ? data : (data.results || []);
        };

        const projectsList = extractData(projectsListRes);
        const blogPostsList = extractData(blogPostsListRes);
        const portfolioItemsList = extractData(portfolioItemsListRes);

        // Load full details for all projects
        console.log('Loading project details...');
        const projectsWithDetails = await Promise.all(
          projectsList.map(async (project) => {
            try {
              const detailRes = await getProject(project.slug);
              return detailRes.data; // Full detail data
            } catch (error) {
              console.error(`Error loading project ${project.slug}:`, error);
              return project; // Fallback to list data
            }
          })
        );

        // Load full details for all portfolio items
        console.log('Loading portfolio item details...');
        const portfolioItemsWithDetails = await Promise.all(
          portfolioItemsList.map(async (item) => {
            try {
              const detailRes = await getPortfolioItem(item.slug);
              return detailRes.data; // Full detail data
            } catch (error) {
              console.error(`Error loading portfolio item ${item.slug}:`, error);
              return item; // Fallback to list data
            }
          })
        );

        // Load full details for all blog posts
        console.log('Loading blog post details...');
        const blogPostsWithDetails = await Promise.all(
          blogPostsList.map(async (post) => {
            try {
              const detailRes = await getBlogPost(post.slug);
              return detailRes.data; // Full detail data
            } catch (error) {
              console.error(`Error loading blog post ${post.slug}:`, error);
              return post; // Fallback to list data
            }
          })
        );

        setData({
          projects: projectsWithDetails,
          categories: extractData(categoriesRes),
          services: extractData(servicesRes),
          blogPosts: blogPostsWithDetails,
          blogCategories: extractData(blogCategoriesRes),
          portfolioItems: portfolioItemsWithDetails,
          portfolioCategories: extractData(portfolioCategoriesRes),
          companyInfo: companyInfoRes.data,
          loading: false,
          error: null
        });

        console.log('All data loaded successfully with full details');
      } catch (error) {
        console.error('Error loading data:', error);
        setData(prev => ({
          ...prev,
          loading: false,
          error: error.message
        }));
      }
    };

    loadAllData();
  }, []); // Only run once on mount

  // Helper functions to filter data
  const getFeaturedProjects = () => {
    return data.projects.filter(p => p.is_featured);
  };

  const getProjectsByCategory = (categorySlug) => {
    if (!categorySlug) return data.projects;
    return data.projects.filter(p => {
      // Handle both category object and category_slug string
      const catSlug = p.category?.slug || p.category_slug;
      return catSlug === categorySlug;
    });
  };

  const getPortfolioItemsByCategory = (categorySlug) => {
    if (!categorySlug) return data.portfolioItems;
    return data.portfolioItems.filter(item => {
      // Handle both category object and category_slug string
      const catSlug = item.category?.slug || item.category_slug;
      return catSlug === categorySlug;
    });
  };

  const getProjectBySlug = (slug) => {
    return data.projects.find(p => p.slug === slug);
  };

  const getBlogPostBySlug = (slug) => {
    return data.blogPosts.find(p => p.slug === slug);
  };

  const getPortfolioItemBySlug = (slug) => {
    return data.portfolioItems.find(item => item.slug === slug);
  };

  const value = {
    ...data,
    getFeaturedProjects,
    getProjectsByCategory,
    getPortfolioItemsByCategory,
    getProjectBySlug,
    getBlogPostBySlug,
    getPortfolioItemBySlug
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};

